<script setup lang="ts">
/**
 * Public demo directory — a single shareable URL (/demos) listing the live
 * demo tenant per vertical pack. Built for sales: open one page, show a
 * prospect a real working agency in their vertical, click through to the
 * live subdomain.
 *
 * Data comes from GET /api/public/tenants (published + active tenants), so
 * it stays in sync with whatever VerticalPackTenantSeeder seeds — no
 * hardcoded list to keep up to date.
 */
definePageMeta({ layout: 'marketing' })

const tenantRepo = useTenantRepository()
const { data: agencies } = await useAsyncData('public-demo-agencies', () =>
  tenantRepo.listPublic()
)

useSeoMeta({
  title: 'Live agency demos — see the product in your vertical',
  description:
    'Explore live demo workspaces for the verticals we support — migration agencies, education agencies, law firms and real estate agencies.'
})
</script>

<template>
  <div class="mx-auto max-w-6xl px-4 py-16 sm:py-20">
    <!-- Hero -->
    <header class="mx-auto max-w-2xl text-center">
      <p class="text-sm font-semibold uppercase tracking-wider text-indigo-600">
        Live demos
      </p>
      <h1 class="mt-3 text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
        See the product running in your vertical
      </h1>
      <p class="mt-4 text-lg leading-relaxed text-gray-600">
        Each agency below is a real, live workspace built on the same platform —
        click through to see how it works for that line of business.
      </p>
    </header>

    <!-- Grid -->
    <section
      v-if="agencies && agencies.length"
      class="mt-12 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3"
    >
      <MarketingAgencyDemoCard
        v-for="agency in agencies"
        :key="agency.slug"
        :agency="agency"
      />
    </section>

    <!-- Empty state -->
    <div
      v-else
      class="mt-12 rounded-2xl border border-dashed border-gray-300 bg-gray-50 px-6 py-16 text-center"
    >
      <p class="text-base font-medium text-gray-900">
        No demos available yet
      </p>
      <p class="mx-auto mt-2 max-w-md text-sm text-gray-500">
        Once demo tenants are published they’ll appear here automatically.
      </p>
    </div>
  </div>
</template>
